/* Created by Interactive 63 - https://interactive63.com */
/**
 * The property registry: every animatable property is defined here once, and the inspector,
 * compiler, sampler and presets all read from it.
 */
import { normaliseColour } from '../colour';
import { GEOMETRY_TAGS, type PropertyKey, type PropertyValue } from '../model/types';
import { TEXT_TAGS, textContentOf } from '../text/text';

export type PropertyGroup = 'transform' | 'appearance' | 'text' | 'stroke' | 'effects' | 'shape';
export type PropertyValueType = 'number' | 'vec2' | 'colour' | 'draw' | 'morph' | 'text';

export interface PropertyDefinition {
  key: PropertyKey;
  label: string;
  /** Plain-language explanation shown as a tooltip on the label. */
  description?: string;
  group: PropertyGroup;
  valueType: PropertyValueType;
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  /** Whether the property can be animated on an element with the given lower-case tag. */
  appliesTo: (tag: string) => boolean;
  /** Reads the element's current visual value, falling back to a neutral value. */
  defaultValue: (el: SVGElement) => PropertyValue;
  /** Whether sampled values are clamped to min and max, as browsers do for opacity values. */
  clampOutput?: boolean;
  /** Needs transform-box/origin and possibly a wrapper group. */
  usesTransform: boolean;
  /** False for "morph", which needs the runtime. */
  cssOnly: boolean;
  /** Shown in the inspector, for example blur support in Safari. */
  browserNote?: string;
}

/** Applies to every element except gradient stops. */
const notStop = (tag: string): boolean => tag !== 'stop';
/** Applies to every element except gradient stops and lines, which have no fill area. */
const notStopOrLine = (tag: string): boolean => tag !== 'stop' && tag !== 'line';
const onlyStop = (tag: string): boolean => tag === 'stop';
const onlyGeometry = (tag: string): boolean => GEOMETRY_TAGS.includes(tag);
const onlyText = (tag: string): boolean => TEXT_TAGS.has(tag);

/** Reads a computed CSS property from an element, or an empty string when unavailable. */
function computed(el: SVGElement, property: string): string {
  return getComputedStyle(el).getPropertyValue(property).trim();
}

/** Reads a numeric computed property, using the fallback when it is missing or not a number. */
function computedNumber(el: SVGElement, property: string, fallback: number): number {
  const value = parseFloat(computed(el, property));
  return Number.isFinite(value) ? value : fallback;
}

/** Reads a computed colour property, using the fallback when it is missing. */
function computedColour(el: SVGElement, property: string, fallback: string): string {
  return normaliseColour(computed(el, property) || fallback);
}

/** Registry entries in display order. */
export const PROPERTIES: Record<PropertyKey, PropertyDefinition> = {
  translate: {
    key: 'translate',
    label: 'Position',
    group: 'transform',
    valueType: 'vec2',
    unit: 'px',
    step: 1,
    appliesTo: notStop,
    defaultValue: () => ({ x: 0, y: 0 }),
    usesTransform: true,
    cssOnly: true,
  },
  rotate: {
    key: 'rotate',
    label: 'Rotation',
    group: 'transform',
    valueType: 'number',
    unit: 'deg',
    step: 1,
    appliesTo: notStop,
    defaultValue: () => 0,
    usesTransform: true,
    cssOnly: true,
  },
  scale: {
    key: 'scale',
    label: 'Scale',
    group: 'transform',
    valueType: 'vec2',
    unit: '×',
    step: 0.01,
    appliesTo: notStop,
    defaultValue: () => ({ x: 1, y: 1 }),
    usesTransform: true,
    cssOnly: true,
  },
  skew: {
    key: 'skew',
    label: 'Skew',
    group: 'transform',
    valueType: 'vec2',
    unit: 'deg',
    min: -89,
    max: 89,
    step: 1,
    appliesTo: notStop,
    defaultValue: () => ({ x: 0, y: 0 }),
    usesTransform: true,
    cssOnly: true,
  },
  opacity: {
    key: 'opacity',
    label: 'Opacity',
    group: 'appearance',
    valueType: 'number',
    min: 0,
    max: 1,
    step: 0.01,
    appliesTo: notStop,
    defaultValue: (el) => computedNumber(el, 'opacity', 1),
    clampOutput: true,
    usesTransform: false,
    cssOnly: true,
  },
  fill: {
    key: 'fill',
    label: 'Fill',
    group: 'appearance',
    valueType: 'colour',
    appliesTo: notStopOrLine,
    defaultValue: (el) => computedColour(el, 'fill', 'black'),
    usesTransform: false,
    cssOnly: true,
  },
  fillOpacity: {
    key: 'fillOpacity',
    label: 'Fill opacity',
    group: 'appearance',
    valueType: 'number',
    min: 0,
    max: 1,
    step: 0.01,
    appliesTo: notStopOrLine,
    defaultValue: (el) => computedNumber(el, 'fill-opacity', 1),
    clampOutput: true,
    usesTransform: false,
    cssOnly: true,
  },
  stroke: {
    key: 'stroke',
    label: 'Stroke',
    group: 'stroke',
    valueType: 'colour',
    appliesTo: notStop,
    defaultValue: (el) => computedColour(el, 'stroke', 'none'),
    usesTransform: false,
    cssOnly: true,
  },
  strokeWidth: {
    key: 'strokeWidth',
    label: 'Stroke width',
    group: 'stroke',
    valueType: 'number',
    unit: 'px',
    min: 0,
    max: 1000,
    step: 0.1,
    appliesTo: notStop,
    defaultValue: (el) => computedNumber(el, 'stroke-width', 1),
    usesTransform: false,
    cssOnly: true,
  },
  strokeOpacity: {
    key: 'strokeOpacity',
    label: 'Stroke opacity',
    group: 'stroke',
    valueType: 'number',
    min: 0,
    max: 1,
    step: 0.01,
    appliesTo: notStop,
    defaultValue: (el) => computedNumber(el, 'stroke-opacity', 1),
    clampOutput: true,
    usesTransform: false,
    cssOnly: true,
  },
  draw: {
    key: 'draw',
    label: 'Stroke reveal',
    description:
      'How much of the outline is drawn, from start % to end %. Used for draw-on effects.',
    group: 'stroke',
    valueType: 'draw',
    unit: '%',
    min: 0,
    max: 100,
    step: 0.1,
    appliesTo: onlyGeometry,
    defaultValue: () => ({ start: 0, end: 100 }),
    usesTransform: false,
    cssOnly: true,
  },
  fontSize: {
    key: 'fontSize',
    label: 'Font size',
    group: 'text',
    valueType: 'number',
    unit: 'px',
    min: 0,
    step: 1,
    appliesTo: onlyText,
    defaultValue: (el) => computedNumber(el, 'font-size', 16),
    usesTransform: false,
    cssOnly: true,
  },
  words: {
    key: 'words',
    label: 'Words',
    description: 'The text shown. Each keyframe switches to its words at that moment.',
    group: 'text',
    valueType: 'text',
    appliesTo: onlyText,
    defaultValue: (el) => textContentOf(el),
    usesTransform: false,
    cssOnly: true,
  },
  blur: {
    key: 'blur',
    label: 'Blur',
    group: 'effects',
    valueType: 'number',
    unit: 'px',
    min: 0,
    max: 200,
    step: 0.1,
    appliesTo: notStop,
    defaultValue: () => 0,
    usesTransform: false,
    cssOnly: true,
    browserNote: 'Blur on individual SVG elements may not render in Safari.',
  },
  stopColor: {
    key: 'stopColor',
    label: 'Stop colour',
    group: 'appearance',
    valueType: 'colour',
    appliesTo: onlyStop,
    defaultValue: (el) => computedColour(el, 'stop-color', 'black'),
    usesTransform: false,
    cssOnly: true,
  },
  stopOpacity: {
    key: 'stopOpacity',
    label: 'Stop opacity',
    group: 'appearance',
    valueType: 'number',
    min: 0,
    max: 1,
    step: 0.01,
    appliesTo: onlyStop,
    defaultValue: (el) => computedNumber(el, 'stop-opacity', 1),
    clampOutput: true,
    usesTransform: false,
    cssOnly: true,
  },
  morph: {
    key: 'morph',
    label: 'Shape morph',
    group: 'shape',
    valueType: 'morph',
    // Other shapes are offered too: adding a morph converts them to a path first.
    appliesTo: onlyGeometry,
    defaultValue: (el) => ({ d: el.getAttribute('d') ?? '' }),
    usesTransform: false,
    cssOnly: false,
  },
};

const PROPERTY_LIST: readonly PropertyDefinition[] = Object.values(PROPERTIES);

/** Returns the definition for a property key. */
export function getProperty(key: PropertyKey): PropertyDefinition {
  return PROPERTIES[key];
}

/** Returns the properties that can be animated on the given tag, in display order. */
export function propertiesForTag(tag: string): PropertyDefinition[] {
  const lower = tag.toLowerCase();
  return PROPERTY_LIST.filter((definition) => definition.appliesTo(lower));
}

/** Whether a property can be animated on an element with the given tag. */
export function isPropertyAllowed(key: PropertyKey, tag: string): boolean {
  return PROPERTIES[key].appliesTo(tag.toLowerCase());
}

/** Whether a string is a known property key. */
export function isPropertyKey(value: string): value is PropertyKey {
  return Object.hasOwn(PROPERTIES, value);
}
